import { skipToken } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Dropdown } from '@sgme/ui';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useContactsQuery } from '@/api/contacts';
import type { ContactInfo } from '@/api/contacts/getContacts.ts';
import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/constants.ts';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTileClientId, selectTileProduct, selectTileProductType } from '@/store/tiles/selectors.ts';

interface TraderInfo {
  name: string;
  team: string;
  email: string | undefined;
  phone: string | undefined;
}

export function TraderInfos({
  tileId,
  traderId,
  contribution,
}: {
  tileId: string;
  traderId?: string | undefined;
  contribution?: 'Automatic' | 'Manual';
}) {
  const { data: contacts } = useContactsQuery(
    isDefined(traderId)
      ? {
          search: traderId,
          fields: ['jobType', 'jobTitle', 'emails', 'phones', 'internalHrTeam'],
        }
      : skipToken,
  );

  const userContext = useUserContext();
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));
  const client = userContext.currentUser.clients.find((client) => client.companyId === clientId);
  const companyName = client?.companyName;

  const product = useAppSelector((state) => selectTileProductType(state, tileId));
  const currencyPair = useAppSelector((state) => selectTileProduct(tileId)(state)?.properties.currencyPair);
  const mailSubject = `${product} ${currencyPair} ${companyName}`;

  const traderInfo = isDefined(contacts) ? mapSgmContactsResultToTraderInfo(contacts) : undefined;

  const copyCb = useCallback(async () => {
    if (traderInfo) {
      await navigator.clipboard.writeText(traderInfo.name);
    }
  }, [traderInfo]);

  if (isNotDefined(traderId)) {
    return null;
  }

  return (
    <Dropdown direction="bottom" color={BACKGROUND_LIGHT_COLOR}>
      <Dropdown.Toggle
        className={`text-truncate d-flex align-content-center badge rounded-pill badge-discreet-${contribution === 'Manual' ? 'info' : 'secondary'}`}
      >
        {contribution === 'Automatic' ? <FormattedMessage id="option.footer.traderInfos.autoQuote" /> : traderId}
      </Dropdown.Toggle>

      {isDefined(traderInfo) && (
        <Dropdown.Menu>
          <div className="p-3 text-info text-nowrap fw-medium">
            <span style={{ cursor: 'copy' }} onClick={copyCb} title="Click to copy name">
              {traderInfo.name}
            </span>{' '}
            <span>{traderInfo.team}</span>
            <br />
            {contribution === 'Manual' && (
              <span className="badge rounded-pill badge-info mt-1">
                <FormattedMessage id="option.footer.traderInfos.liveBadge" />
              </span>
            )}
          </div>

          {isDefined(traderInfo.email) && (
            <Dropdown.Item href={`sip:${traderInfo.email}`}>
              <FormattedMessage id="option.footer.traderInfo.actions.chat" />
            </Dropdown.Item>
          )}

          {isDefined(traderInfo.phone) && (
            <Dropdown.Item href={`tel:${traderInfo.phone}`}>
              <FormattedMessage id="option.footer.traderInfo.actions.call" />
            </Dropdown.Item>
          )}

          {isDefined(traderInfo.email) && (
            <Dropdown.Item href={`mailto:${traderInfo.email}?subject=${mailSubject}`}>
              <FormattedMessage id="option.footer.traderInfo.actions.email" />
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
}

function findTraderContact(contacts: readonly ContactInfo[]): ContactInfo | undefined {
  if (contacts.length === 1) {
    return contacts[0];
  }
  if (contacts.length > 1) {
    const traders = contacts.filter(
      (c) =>
        c.jobTitle.toLowerCase().includes('trader') ||
        c.jobTitle.toLowerCase().includes(' fx ') ||
        c.jobType.toLowerCase().includes('trading'),
    );
    if (traders.length === 1) {
      return traders[0];
    }
  }
  return undefined;
}

function mapSgmContactsResultToTraderInfo(contacts: ContactInfo[]): TraderInfo | undefined {
  const contact = findTraderContact(contacts);
  if (contact === undefined) {
    console.warn('Could not find trader contact');
    return undefined;
  }
  return {
    name: contact.fullName,
    team: contact.internalHrTeam ?? '',
    email: contact.emails?.find((email) => email.type === 'Professional')?.value,
    phone: contact.phones?.find((phone) => phone.type === 'Business')?.value,
  };
}
